import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getAgentById, fetchData } from "../../api/client";
import { getPaymentMethodTipoLabel } from "../../constants/paymentMethodTipo";
import { useAgents } from "../../hooks/useAgents";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import { useS3Upload } from "../../hooks/useS3Upload";
import SkillSelector from "../../components/Agents/SkillSelector";
import AgentStatusToggle from "../../components/Agents/AgentStatusToggle";
import AgentDocumentsSection from "../../components/Agents/AgentDocumentsSection";

const MODEL_OPTIONS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (rápido e econômico)" },
  { value: "gpt-4.1", label: "GPT-4.1" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
];

// Limites usados pelo RAG do backend (agentRagService / faqSkill)
const MAX_RAG_DOCS = 8; // loadAgentKnowledgeChunks: documents.slice(0, 8)
const MAX_RAG_URLS = 6; // loadAgentKnowledgeChunks: urls.slice(0, 6)

/** Converte status do backend para boolean (toggle). */
const statusToBoolean = (v) => v === "active" || v === true;

/** Converte boolean para status do backend. */
const booleanToStatus = (v) => (v ? "active" : "inactive");

function coerceSkillConfig(raw) {
  if (raw == null || raw === "") return {};
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === "object" && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
  return {};
}

/** Normaliza skills do backend (array de { skill_key, config?, order_index? }) para o form. */
const normalizeSkillsFromBackend = (raw) => {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s) => {
      if (typeof s !== "object" || !s?.skill_key) return null;
      return { ...s, config: coerceSkillConfig(s.config) };
    })
    .filter(Boolean)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
};

/** Mesmo shape que `usePaymentMethods` usa ao listar `/payment-methods`. */
function normalizePaymentMethodsListResponse(response) {
  const payload = response ?? {};
  const inner = payload.data ?? {};
  if (Array.isArray(inner.data)) return inner.data;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

const AgentForm = () => {
  const { t } = useTranslation("settings");
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { segment, isLoading: isLoadingSegment } = useWorkspaceSegment();
  const showTicketSellerSkillOption =
    isLoadingSegment || segment === WorkspaceSegments.PassengerTransport;
  const canConfigureTicketSeller =
    !isLoadingSegment && segment === WorkspaceSegments.PassengerTransport;

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canUpdate = can(scopeSet, "agents", "update");
  const canCreate = can(scopeSet, "agents", "create");
  const canViewPaymentMethods = can(scopeSet, "payment_methods", "view");
  const readOnly = isEdit && !canUpdate;

  const [name, setName] = useState("");
  const [engineType, setEngineType] = useState("xpro"); // 'xpro' ou 'n8n'
  const [model, setModel] = useState("gpt-4o-mini");
  const [temperature, setTemperature] = useState(0.7);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [n8nWebhookUrl, setN8nWebhookUrl] = useState("");
  const [n8nHeadersJson, setN8nHeadersJson] = useState("{\n  \"X-API-KEY\": \"seu-token-aqui\"\n}");
  const [status, setStatus] = useState(true);
  const [skills, setSkills] = useState([{ skill_key: "receptionist", config: {}, order_index: 0 }]);
  const [sourceUrls, setSourceUrls] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [documents, setDocuments] = useState([]); // { id, file?, url?, name }[]
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [submitError, setSubmitError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const primarySkillKey = useMemo(() => {
    const s = skills[0];
    return s && typeof s === "object" && s.skill_key ? String(s.skill_key) : null;
  }, [skills]);

  const ticketSellerCfg = useMemo(() => {
    if (primarySkillKey !== "ticket_seller") return {};
    const s = skills[0];
    const c = s?.config;
    return c && typeof c === "object" && !Array.isArray(c) ? c : {};
  }, [skills, primarySkillKey]);

  const faqCfg = useMemo(() => {
    if (primarySkillKey !== "faq") return {};
    const s = skills[0];
    const c = s?.config;
    return c && typeof c === "object" && !Array.isArray(c) ? c : {};
  }, [skills, primarySkillKey]);

  const ticketSellerEnabled =
    engineType === "xpro" && primarySkillKey === "ticket_seller" && canConfigureTicketSeller;

  const { data: paymentMethodsList = [], isLoading: paymentMethodsLoading, isError: paymentMethodsError } = useQuery(
    ["agentFormPaymentMethods", canViewPaymentMethods],
    () => fetchData(`/payment-methods?page=1&limit=500&search=`),
    {
      enabled: ticketSellerEnabled && canViewPaymentMethods,
      staleTime: 60_000,
      select: normalizePaymentMethodsListResponse,
    }
  );

  const paymentMethodsForSelect = useMemo(() => {
    const rows = Array.isArray(paymentMethodsList) ? paymentMethodsList : [];
    const active = rows.filter((m) => m && Number(m.status) === 1);
    active.sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), undefined, { sensitivity: "base" }));
    return active;
  }, [paymentMethodsList]);

  const selectedFormaIdStr = useMemo(() => {
    const v = ticketSellerCfg.id_formadepagamento;
    if (v == null || v === "") return "";
    return String(v).trim();
  }, [ticketSellerCfg.id_formadepagamento]);

  const orphanFormaRow = useMemo(() => {
    if (!selectedFormaIdStr) return null;
    const rows = Array.isArray(paymentMethodsList) ? paymentMethodsList : [];
    return rows.find((m) => m && String(m.id) === selectedFormaIdStr) || null;
  }, [paymentMethodsList, selectedFormaIdStr]);

  const mergeSkillConfig = React.useCallback((skillKey, partial) => {
    setSkills((prev) => {
      const row = prev[0];
      if (!row || row.skill_key !== skillKey) return prev;
      const base = row.config && typeof row.config === "object" && !Array.isArray(row.config) ? { ...row.config } : {};
      return [{ ...row, config: { ...base, ...partial } }];
    });
  }, []);

  const fileInputRef = React.useRef(null);
  const documentIdRef = React.useRef(0);
  const multiSkillMigratedRef = React.useRef(false);

  // O backend usa `mammoth` para DOCX. O formato `.doc` legacy tende a falhar/retornar texto vazio.
  // Por isso, evitamos o upload de `.doc` no frontend.
  const ACCEPT_DOCS = ".pdf,.docx,.xls,.xlsx";
  const { uploadMany: uploadManyDocs } = useS3Upload();

  const buildDocumentsPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `agents/documents/${yyyy}/${mm}`;
  };

  const handleAddUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    try {
      new URL(url);
    } catch {
      setSnack({ open: true, severity: "warning", message: "Informe uma URL válida." });
      return;
    }
    setSourceUrls((prev) => {
      if (prev.includes(url)) return prev;
      if (prev.length >= MAX_RAG_URLS) {
        setSnack({
          open: true,
          severity: "warning",
          message: `Limite de ${MAX_RAG_URLS} links de sites por agente. Remova links antes de adicionar mais.`,
        });
        return prev;
      }
      return [...prev, url];
    });
    setNewUrl("");
  };

  const handleRemoveUrl = (index) => {
    setSourceUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const validateDocFiles = (fileList) => {
    const exts = [".pdf", ".docx", ".xls", ".xlsx"];
    const accepted = [];
    const errors = [];
    const MAX_MB = 15;
    Array.from(fileList || []).forEach((f) => {
      const name = (f && f.name) || "";
      const ext = name.toLowerCase().slice(name.lastIndexOf("."));
      if (ext === ".doc") {
        errors.push(`${name || "arquivo"}: formato .doc legacy não suportado. Use .docx.`);
        return;
      }
      const sizeOk = f && typeof f.size === "number" && f.size <= MAX_MB * 1024 * 1024;
      if (!exts.includes(ext)) {
        errors.push(`${name || "arquivo"}: tipo inválido (apenas PDF, Word (.docx) e planilhas).`);
      } else if (!sizeOk) {
        errors.push(`${name || "arquivo"}: maior que ${MAX_MB}MB.`);
      } else {
        accepted.push(f);
      }
    });
    if (errors.length) {
      setSnack({ open: true, severity: "warning", message: errors.slice(0, 2).join(" ") });
    }
    return accepted;
  };

  const addDocFiles = (fileList) => {
    const files = validateDocFiles(fileList);
    if (!files.length) return;

    const availableSlots = Math.max(0, MAX_RAG_DOCS - documents.length);
    if (availableSlots <= 0) {
      setSnack({
        open: true,
        severity: "warning",
        message: `Limite de ${MAX_RAG_DOCS} documentos por agente. Remova documentos antes de adicionar mais.`,
      });
      return;
    }

    const trimmed = files.slice(0, availableSlots);
    if (trimmed.length !== files.length) {
      setSnack({
        open: true,
        severity: "warning",
        message: `Foram ignorados ${files.length - trimmed.length} documentos por exceder o limite (${MAX_RAG_DOCS}).`,
      });
    }
    setDocuments((prev) => [
      ...prev,
      ...trimmed.map((file) => ({
        id: `tmp_${++documentIdRef.current}`,
        file,
        name: file.name,
      })),
    ]);
  };

  const handleClickAddDocs = () => fileInputRef.current?.click();

  const handleDropDocs = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addDocFiles(e.dataTransfer.files);
  };

  const handleDragOverDocs = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const moveDocument = (idx, dir) => {
    setDocuments((prev) => {
      const list = [...prev];
      const to = idx + dir;
      if (to < 0 || to >= list.length) return prev;
      const [it] = list.splice(idx, 1);
      list.splice(to, 0, it);
      return list;
    });
  };

  const removeDocument = (idx) => {
    setDocuments((prev) => prev.filter((_, i) => i !== idx));
  };

  const { data: agentData, isLoading: loadingAgent } = useQuery(
    ["agent", id],
    () => getAgentById(id),
    { enabled: isEdit }
  );

  const agent = agentData?.data ?? agentData;

  useEffect(() => {
    if (!agent || !isEdit) return;
    setName(agent.name ?? agent.nome ?? "");
    const agentModel = agent.model ?? agent.modelo ?? "gpt-4o-mini";
    setModel(agentModel);
    setTemperature(Number(agent.temperature) ?? 0.7);
    const rawSystemPrompt = agent.system_prompt ?? agent.prompt ?? agent.prompt_base ?? "";
    // Detecta agente N8N pelo model
    if (String(agentModel).toLowerCase() === "n8n") {
      setEngineType("n8n");
      // Para N8N, esperamos JSON em system_prompt com config do webhook
      try {
        const parsed = typeof rawSystemPrompt === "string" ? JSON.parse(rawSystemPrompt || "{}") : rawSystemPrompt || {};
        if (parsed && typeof parsed === "object") {
          if (parsed.webhook_url) setN8nWebhookUrl(parsed.webhook_url);
          if (parsed.headers) {
            try {
              setN8nHeadersJson(JSON.stringify(parsed.headers, null, 2));
            } catch {
              setN8nHeadersJson("{\n  \"X-API-KEY\": \"seu-token-aqui\"\n}");
            }
          }
        }
      } catch {
        // Se não for JSON válido, mantém campos padrão
      }
      // Para agentes N8N o system_prompt é usado como config JSON, então não exibimos no campo de texto
      setSystemPrompt("");
    } else {
      setEngineType("xpro");
      setSystemPrompt(rawSystemPrompt);
    }
    setStatus(statusToBoolean(agent.status ?? agent.enabled ?? agent.is_active));
    const rawSkills = normalizeSkillsFromBackend(agent.skills);
    const sortedSkills = [...rawSkills].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
    if (sortedSkills.length > 1) {
      setSkills([sortedSkills[0]]);
      if (!multiSkillMigratedRef.current) {
        multiSkillMigratedRef.current = true;
        setSnack({ open: true, severity: "info", message: t("agentsListPage.skillMultiMigratedHint") });
      }
    } else if (sortedSkills.length === 1) {
      setSkills(sortedSkills);
    } else {
      setSkills([{ skill_key: "receptionist", config: {}, order_index: 0 }]);
    }
    const urls = agent.source_urls ?? agent.urls ?? agent.reference_urls;
    setSourceUrls(Array.isArray(urls) ? urls.filter(Boolean) : []);
    const docs = agent.documents ?? agent.documentos ?? [];
    setDocuments(
      Array.isArray(docs)
        ? docs.map((d) => ({
            id: d.id,
            url: d.file_url ?? d.url,
            name: d.file_name ?? d.name ?? "documento",
          }))
        : []
    );
  }, [agent, isEdit, t]);

  useEffect(() => {
    multiSkillMigratedRef.current = false;
  }, [id]);

  useEffect(() => {
    if (isLoadingSegment) return;
    if (segment === WorkspaceSegments.PassengerTransport) return;
    if (primarySkillKey !== "ticket_seller") return;
    setSkills([{ skill_key: "receptionist", config: {}, order_index: 0 }]);
  }, [isLoadingSegment, segment, primarySkillKey]);

  useEffect(() => {
    if (engineType !== "xpro") return;
    setSkills((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return [{ skill_key: "receptionist", config: {}, order_index: 0 }];
      }
      if (prev.length > 1) {
        const sorted = [...prev].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
        return [sorted[0]];
      }
      return prev;
    });
  }, [engineType]);

  useEffect(() => {
    const onAddFiles = (ev) => {
      const files = ev.detail || [];
      addDocFiles(files);
    };
    window.addEventListener("agent-documents:addFiles", onAddFiles);
    return () => window.removeEventListener("agent-documents:addFiles", onAddFiles);
  }, []);

  const { createMutation, updateMutation } = useAgents();
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Validações de consistência com o RAG do backend.
    // Isso evita criar/editar um agente "configurado", mas que não consegue extrair conhecimento.
    if (engineType === "xpro") {
      if (!Array.isArray(skills) || skills.length !== 1 || !skills[0]?.skill_key) {
        const msg = t("agentsListPage.skillExactlyOneError");
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }

      if (
        skills[0].skill_key === "ticket_seller" &&
        !isLoadingSegment &&
        segment !== WorkspaceSegments.PassengerTransport
      ) {
        const msg =
          "A capacidade de venda de passagens (BPe) só está disponível em workspaces do segmento transporte rodoviário de passageiros.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }

      const usesFaqRag = skills[0].skill_key === "faq";
      if (usesFaqRag) {
        if (documents.length > MAX_RAG_DOCS) {
          const msg = `Você excedeu o limite de ${MAX_RAG_DOCS} documentos. O FAQ só considera os primeiros ${MAX_RAG_DOCS}. Remova os excedentes.`;
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }

        if (sourceUrls.length > MAX_RAG_URLS) {
          const msg = `Você excedeu o limite de ${MAX_RAG_URLS} links de sites. O FAQ só considera os primeiros ${MAX_RAG_URLS}. Remova os excedentes.`;
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }

        const hasLegacyDoc = documents.some((d) => String(d?.name || "").toLowerCase().endsWith(".doc"));
        if (hasLegacyDoc) {
          const msg =
            "Existem documentos no formato .doc legacy. O RAG do FAQ usa mammoth e recomenda .docx. Remova/substitua por .docx.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }
      }
    }

    if (engineType === "n8n") {
      const url = String(n8nWebhookUrl || "").trim();
      if (!url) {
        const msg = "Informe a URL do webhook N8N.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }
      try {
        // valida formato básico da URL
        new URL(url);
      } catch {
        const msg = "URL do webhook N8N inválida.";
        setSubmitError(msg);
        setSnack({ open: true, severity: "warning", message: msg });
        return;
      }
      if (n8nHeadersJson) {
        try {
          JSON.parse(n8nHeadersJson);
        } catch {
          const msg = "Headers JSON inválidos. Corrija o JSON ou deixe em branco.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "warning", message: msg });
          return;
        }
      }
    }

    const buildPayload = (docUrls) => {
      // Monta system_prompt / modelo conforme o tipo do agente
      let finalModel = model;
      let finalSystemPrompt = systemPrompt.trim() || null;

      if (engineType === "n8n") {
        finalModel = "n8n";
        let headersObj = {};
        try {
          headersObj = n8nHeadersJson ? JSON.parse(n8nHeadersJson) : {};
        } catch {
          headersObj = {};
        }
        finalSystemPrompt = JSON.stringify(
          {
            webhook_url: n8nWebhookUrl?.trim() || "",
            headers: headersObj,
            timeout_ms: 15000,
          },
          null,
          2
        );
      }

      return {
        name: name.trim() || (agent?.nome ?? agent?.name),
        status: booleanToStatus(status),
        model: finalModel,
        temperature,
        system_prompt: finalSystemPrompt,
        skills: Array.isArray(skills) && skills[0]?.skill_key ? [skills[0]] : [],
        source_urls: sourceUrls.length ? sourceUrls.filter((u) => u && String(u).trim()) : undefined,
        documents: docUrls.length ? docUrls : undefined,
      };
    };

    const novos = documents.filter((d) => d.file);
    let uploadResults = [];

    if (novos.length) {
      try {
        setUploadProgress({ done: 0, total: novos.length });
        uploadResults = await uploadManyDocs(
          novos.map((n) => n.file),
          { prefix: buildDocumentsPrefix() },
          (done, total) => setUploadProgress({ done, total })
        );
        uploadResults.forEach((r, i) => {
          if (!r?.ok || !r?.url) throw new Error(r?.error || `Falha no upload de "${novos[i]?.name}"`);
        });
      } catch (err) {
        setSubmitError(err?.message || "Falha ao enviar documentos.");
        setSnack({ open: true, severity: "error", message: err?.message || "Falha ao enviar documentos." });
        return;
      } finally {
        setUploadProgress({ done: 0, total: 0 });
      }
    }

    const docUrls = documents.map((d) => {
      if (d.url) return { url: d.url, file_name: d.name };
      const i = novos.indexOf(d);
      if (i === -1) return null;
      const r = uploadResults[i];
      return r?.url ? { url: r.url, file_name: d.name } : null;
    }).filter(Boolean);

    const payload = buildPayload(docUrls);

    if (isEdit) {
      updateMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            setSnack({ open: true, severity: "success", message: "Agente de IA atualizado." });
            navigate(`/agents/${id}`);
          },
          onError: (err) => {
            const msg = err?.message || "Erro ao atualizar o agente de IA.";
            setSubmitError(msg);
            setSnack({ open: true, severity: "error", message: msg });
          },
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res) => {
          const created = res?.data ?? res;
          const newId = created?.id ?? created?.id_agent;
          setSnack({ open: true, severity: "success", message: "Agente de IA criado." });
          if (newId) navigate(`/agents/${newId}`);
          else navigate("/agents");
        },
        onError: (err) => {
          const msg = err?.message || "Erro ao criar o agente de IA.";
          setSubmitError(msg);
          setSnack({ open: true, severity: "error", message: msg });
        },
      });
    }
  };

  if (isEdit && loadingAgent) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Carregando agente de IA...</Typography>
      </Box>
    );
  }

  const sectionTitleSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1.5,
    fontWeight: 600,
    fontSize: "1rem",
    color: "text.primary",
  };

  return (
    <Box className="tour-agent-form" sx={{ paddingTop: 1, paddingBottom: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 3 },
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/agents")}>
            Voltar
          </Button>
          {isEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<BarChartRoundedIcon />}
              onClick={() => navigate(`/agents/${id}/analytics`)}
            >
              Estatísticas
            </Button>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {isEdit ? "Editar agente de IA" : "Novo agente de IA"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isEdit
            ? "Altere os dados abaixo e salve quando terminar."
            : "Preencha o nome e as capacidades. O resto é opcional."}
        </Typography>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
          {/* 1. Identificação */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography sx={sectionTitleSx}>
              <BadgeOutlinedIcon fontSize="small" color="primary" />
              Identificação
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Nome do agente de IA"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                size="small"
                placeholder="Ex.: Atendente de vendas"
                helperText="Um nome que você reconheça na lista de agentes de IA"
                inputProps={{ readOnly }}
              />
            </Stack>
          </Paper>

          {/* 2. Capacidades e status */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography sx={sectionTitleSx}>
              <PsychologyOutlinedIcon fontSize="small" color="primary" />
              Capacidades
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Tipo de agente</InputLabel>
                <Select
                  label="Tipo de agente"
                  value={engineType}
                  onChange={(e) => setEngineType(e.target.value)}
                >
                  <MenuItem value="xpro">Agente Xpro (modelo de IA interno)</MenuItem>
                  <MenuItem value="n8n">Agente N8N (fluxo externo)</MenuItem>
                </Select>
              </FormControl>
              {engineType === "xpro" && (
                <SkillSelector
                  value={skills}
                  onChange={setSkills}
                  disabled={readOnly}
                  showTicketSellerSkill={showTicketSellerSkillOption}
                  includeRoadPassengerCopy={canConfigureTicketSeller}
                />
              )}
              {engineType === "xpro" && skills.length === 1 && primarySkillKey && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t("agentsListPage.skillParamsIntro")}
                  </Typography>
                  {primarySkillKey === "receptionist" && (
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t("agentsListPage.skillParamsReceptionistTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                        {t(
                          canConfigureTicketSeller
                            ? "agentsListPage.skillParamsReceptionistBody"
                            : "agentsListPage.skillParamsReceptionistBodyGeneric"
                        )}
                      </Typography>
                    </Stack>
                  )}
                  {primarySkillKey === "ticket_seller" && canConfigureTicketSeller && (
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t("agentsListPage.skillParamsPassengerTitle")}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ticketSellerCfg.require_email_for_bpe !== false}
                            onChange={(e) =>
                              mergeSkillConfig("ticket_seller", { require_email_for_bpe: e.target.checked })
                            }
                            disabled={readOnly}
                          />
                        }
                        label={t("agentsListPage.skillParamsRequireEmailBpe")}
                      />
                      {canViewPaymentMethods && !paymentMethodsError ? (
                        <FormControl fullWidth size="small" disabled={readOnly || paymentMethodsLoading}>
                          <InputLabel id="agent-ticket-forma-pagamento-label">
                            {t("agentsListPage.skillParamsIdFormaPagamento")}
                          </InputLabel>
                          <Select
                            labelId="agent-ticket-forma-pagamento-label"
                            label={t("agentsListPage.skillParamsIdFormaPagamento")}
                            value={selectedFormaIdStr}
                            onChange={(e) => {
                              const v = e.target.value;
                              mergeSkillConfig("ticket_seller", {
                                id_formadepagamento: v === "" ? "" : String(v),
                              });
                            }}
                          >
                            <MenuItem value="">
                              <em>{t("agentsListPage.skillParamsFormaPagamentoDefault")}</em>
                            </MenuItem>
                            {paymentMethodsForSelect.map((m) => (
                              <MenuItem key={m.id} value={String(m.id)}>
                                {m.nome || "—"} — {getPaymentMethodTipoLabel(m.tipo)} (ID {m.id})
                              </MenuItem>
                            ))}
                            {selectedFormaIdStr &&
                              !paymentMethodsForSelect.some((m) => String(m.id) === selectedFormaIdStr) && (
                                <MenuItem value={selectedFormaIdStr}>
                                  {orphanFormaRow
                                    ? `${orphanFormaRow.nome || "—"} — ${getPaymentMethodTipoLabel(orphanFormaRow.tipo)} (ID ${orphanFormaRow.id}, ${t("agentsListPage.skillParamsFormaPagamentoInactive")})`
                                    : t("agentsListPage.skillParamsFormaPagamentoUnknownId", { id: selectedFormaIdStr })}
                                </MenuItem>
                              )}
                          </Select>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            {t("agentsListPage.skillParamsIdFormaPagamentoHelper")}
                          </Typography>
                          {paymentMethodsLoading && (
                            <Typography variant="caption" color="text.secondary">
                              {t("agentsListPage.skillParamsFormaPagamentoLoading")}
                            </Typography>
                          )}
                        </FormControl>
                      ) : (
                        <>
                          {!canViewPaymentMethods && (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                              {t("agentsListPage.skillParamsFormaPagamentoNoListPermission")}
                            </Alert>
                          )}
                          {paymentMethodsError && canViewPaymentMethods && (
                            <Alert severity="warning" sx={{ py: 0.5 }}>
                              {t("agentsListPage.skillParamsFormaPagamentoLoadError")}
                            </Alert>
                          )}
                          <TextField
                            label={t("agentsListPage.skillParamsIdFormaPagamentoManual")}
                            helperText={t("agentsListPage.skillParamsIdFormaPagamentoHelper")}
                            value={ticketSellerCfg.id_formadepagamento != null ? String(ticketSellerCfg.id_formadepagamento) : ""}
                            onChange={(e) =>
                              mergeSkillConfig("ticket_seller", {
                                id_formadepagamento: e.target.value.trim() === "" ? "" : e.target.value.trim(),
                              })
                            }
                            fullWidth
                            size="small"
                            disabled={readOnly}
                          />
                        </>
                      )}
                      <TextField
                        label={t("agentsListPage.skillParamsBpeFlowNote")}
                        value={ticketSellerCfg.bpe_flow_note || ""}
                        onChange={(e) => mergeSkillConfig("ticket_seller", { bpe_flow_note: e.target.value })}
                        multiline
                        minRows={2}
                        fullWidth
                        size="small"
                        disabled={readOnly}
                      />
                    </Stack>
                  )}
                  {primarySkillKey === "faq" && (
                    <Stack spacing={1}>
                      <TextField
                        label={t("agentsListPage.skillParamsFaqFocus")}
                        helperText={t("agentsListPage.skillParamsFaqFocusHelper")}
                        value={faqCfg.faq_answer_focus || ""}
                        onChange={(e) => mergeSkillConfig("faq", { faq_answer_focus: e.target.value })}
                        multiline
                        minRows={3}
                        fullWidth
                        size="small"
                        disabled={readOnly}
                      />
                    </Stack>
                  )}
                </Paper>
              )}
              {engineType === "xpro" && primarySkillKey && primarySkillKey !== "faq" && (
                <Alert severity="info" sx={{ py: 0.75 }}>
                  {t("agentsListPage.knowledgeNotForThisSkill")}
                </Alert>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Agente de IA ativo?
                </Typography>
                <AgentStatusToggle value={status} onChange={setStatus} disabled={isEdit && !canUpdate} />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Inativo = não aparece em solicitações até você ativar.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* 3. Fontes de conhecimento — só o FAQ (Xpro) usa RAG em documentos/links; N8N segue opcional */}
          {(engineType === "n8n" || (engineType === "xpro" && primarySkillKey === "faq")) && (
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography sx={sectionTitleSx}>
                <MenuBookOutlinedIcon fontSize="small" color="primary" />
                Fontes de conhecimento
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {engineType === "n8n"
                  ? "Documentos e links opcionais (o fluxo N8N não os recebe automaticamente do Xpro)."
                  : t("agentsListPage.knowledgeSectionFaqIntro")}
              </Typography>
              {engineType === "n8n" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Para agentes N8N, documentos/URLs não são enviados automaticamente para o seu fluxo. Use-os apenas se o seu
                  N8N buscar essas informações por conta própria.
                </Alert>
              )}
              <Stack spacing={2}>
                <AgentDocumentsSection
                  documents={documents}
                  fileInputRef={fileInputRef}
                  handleClickAdd={handleClickAddDocs}
                  handleDrop={handleDropDocs}
                  handleDragOver={handleDragOverDocs}
                  moveDocument={moveDocument}
                  removeDocument={removeDocument}
                  readOnly={readOnly}
                  accept={ACCEPT_DOCS}
                  embedded
                />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    <LinkIcon fontSize="small" />
                    Links de sites
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="https://..."
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddUrl())}
                      disabled={readOnly}
                      sx={{ minWidth: 260, flex: 1 }}
                    />
                    <Button variant="outlined" size="small" startIcon={<AddRoundedIcon />} onClick={handleAddUrl} disabled={readOnly}>
                      Adicionar
                    </Button>
                  </Stack>
                  {sourceUrls.length > 0 && (
                    <List dense disablePadding sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
                      {sourceUrls.map((url, index) => (
                        <ListItem key={`${url}-${index}`}>
                          <ListItemText primary={url} primaryTypographyProps={{ variant: "body2", noWrap: true }} />
                          {!readOnly && (
                            <ListItemSecondaryAction>
                              <IconButton size="small" edge="end" onClick={() => handleRemoveUrl(index)} aria-label="Remover URL">
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Stack>
            </Paper>
          )}

          {/* 4. Avançado */}
          <Accordion
            defaultExpanded={false}
            sx={{
              boxShadow: "none",
              "&:before": { display: "none" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: 500 }}>
                <SettingsOutlinedIcon fontSize="small" />
                Configurações avançadas
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ flexDirection: "column", gap: 2, display: "flex", pt: 0 }}>
              {engineType === "xpro" && (
                <>
                  <FormControl fullWidth size="small" disabled={readOnly}>
                    <InputLabel>Modelo de IA</InputLabel>
                    <Select label="Modelo de IA" value={model} onChange={(e) => setModel(e.target.value)}>
                      {MODEL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography gutterBottom variant="body2">
                      Criatividade (temperatura)
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, v) => setTemperature(v)}
                      min={0}
                      max={2}
                      step={0.1}
                      valueLabelDisplay="auto"
                      disabled={readOnly}
                      marks={[
                        { value: 0, label: "0" },
                        { value: 1, label: "1" },
                        { value: 2, label: "2" },
                      ]}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Mais alto = respostas mais variadas.
                    </Typography>
                  </Box>
                  <TextField
                    label="Instruções para o agente de IA"
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    placeholder="Ex.: Você é um atendente prestativo. Responda em português de forma clara."
                    helperText="Opcional — tom e regras gerais"
                    inputProps={{ readOnly }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
                    O sistema anexa automaticamente ao contexto do modelo o momento do cliente na empresa (nome da etapa de
                    consciência cadastrada em Configurações, vinculada ao cliente) ou indica explicitamente quando esse nível não
                    está definido, para a IA calibrar tom e próximo passo. Não é necessário repetir isso nas instruções acima.
                  </Typography>
                </>
              )}
              {engineType === "n8n" && (
                <>
                  <TextField
                    label="URL do webhook N8N"
                    value={n8nWebhookUrl}
                    onChange={(e) => setN8nWebhookUrl(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="https://seu-n8n.com/webhook/xpro-atendimento"
                    helperText="Endpoint HTTP do fluxo N8N que vai receber as mensagens desta conversa."
                    inputProps={{ readOnly }}
                  />
                  <TextField
                    label="Headers JSON para o N8N (opcional)"
                    value={n8nHeadersJson}
                    onChange={(e) => setN8nHeadersJson(e.target.value)}
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    placeholder='{\n  "X-API-KEY": "seu-token-aqui"\n}'
                    helperText="Headers adicionais enviados na requisição para o N8N (formato JSON)."
                    inputProps={{ readOnly }}
                  />
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Como configurar o fluxo no N8N
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      1. Crie um fluxo com um nó Webhook usando exatamente a URL configurada acima.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      2. O webhook recebe um JSON com: <code>conversationId</code>, <code>message</code>, <code>agent</code>, <code>meta</code> e <code>company</code>. Em <code>meta</code> podem vir{" "}
                      <code>clientAwarenessStageLabel</code> (nome da etapa de consciência ou “não definido”) e{" "}
                      <code>clientAwarenessPromptBlock</code> (texto já formatado para contexto).
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      3. Processe a mensagem no fluxo (nós de função, HTTP, banco, etc.) e, no final, faça o nó de resposta HTTP devolver um JSON com
                      um dos campos: <code>reply</code>, <code>output</code> ou <code>message</code> contendo o texto que será enviado ao cliente.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      4. Garanta que o fluxo responda sempre com HTTP 2xx para o Xpro considerar a chamada bem sucedida.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      O agente N8N ignora modelo de IA e temperatura locais; toda a lógica e integrações ficam dentro do fluxo no N8N.
                    </Typography>
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Ações */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap", mt: 3 }}>
            <Button type="button" variant="outlined" onClick={() => navigate("/agents")} disabled={isSaving}>
              {isEdit ? "Voltar" : "Cancelar"}
            </Button>
            {(isEdit ? canUpdate : canCreate) && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSaving || !name.trim() || (uploadProgress.total > 0 && uploadProgress.done < uploadProgress.total)}
              >
                {uploadProgress.total > 0 ? (
                  `Enviando ${uploadProgress.done}/${uploadProgress.total}...`
                ) : isSaving ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Salvando...
                  </>
                ) : isEdit ? (
                  "Salvar alterações"
                ) : (
                  "Criar agente de IA"
                )}
              </Button>
            )}
          </Box>
        </Stack>
      </form>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default AgentForm;
